import { useState } from "react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useStable } from "../lib/useStable";
import { useViewer } from "../lib/viewer";
import { Card } from "@/components/ui/card";
import { Loading, Empty, ScreenBoundary } from "../components/states";
import {
  SectionHeading,
  StatusPill,
  UrgencyTag,
  Chips,
  relativeDay,
  statusChips,
} from "../components/kit";
import { countOpen } from "@shared/openRequests";

/**
 * The request list — read-only history.
 *
 * An assistant's "did anyone do anything about it?" screen. Deliberately not
 * actionable: deciding lines is the manager's job on /approvals, and putting
 * the same controls in two places is how two people end up disagreeing about
 * who cancelled something.
 *
 * The row is one shape at both widths, not two. On a phone the quantity and
 * name sit on one line with the status underneath; at `lg` the same blocks
 * flow onto a single line with the status and the provenance pushed right, so
 * a screenful is twenty lines rather than eight. A promo note wraps to its own
 * row at both widths, because it is the one field with a sentence in it.
 */

type Filter = "open" | "requested" | "approved" | "ordered-vendor" | "back-ordered" | "received" | "all";

export function Requests() {
  // Remounting the boundary is how a failed query gets retried: Convex throws
  // during render, so there is nothing to re-run without a fresh subtree.
  const [attempt, setAttempt] = useState(0);
  return (
    <ScreenBoundary
      key={attempt}
      title="Requests could not load"
      heading={<SectionHeading title="Requests" />}
      onRetry={() => setAttempt((n) => n + 1)}
    >
      <RequestsScreen />
    </ScreenBoundary>
  );
}

function RequestsScreen() {
  const viewer = useViewer();
  const counts = useQuery(api.requests.statusCounts, {});
  const [filter, setFilter] = useState<Filter>("open");

  /* `useStable`: the chip row drives this query's arguments, so without it
     every tap returns `undefined` for a beat and the guard below blanks the
     whole screen — chips included — back to a skeleton. First load still
     shows one; a filter change keeps the list until the new one lands. */
  const lines = useStable(
    useQuery(api.requests.list, {
      openOnly: filter === "open",
      status: filter === "open" || filter === "all" ? undefined : filter,
    }),
  );

  if (lines === undefined) {
    return (
      <div data-screen-state="loading">
        <Loading rows={5} label="Loading requests" />
      </div>
    );
  }

  // One definition, in shared/openRequests.ts. This used to restate the
  // arithmetic and dropped back-ordered, so the header undercounted the list
  // directly beneath it.
  const openCount = countOpen(counts);

  return (
    <div data-screen-state="ready" className="space-y-5 pb-8">
      <SectionHeading
        title={
          viewer.clinicName ? `${viewer.clinicName} requests` : "All requests"
        }
        subtitle={`${openCount} still open · ${counts?.received ?? 0} received`}
      />

      <Chips<Filter>
        value={filter}
        onChange={setFilter}
        options={[
          { value: "open", label: "Open", count: openCount },
          // This screen offers the whole life of a line; Approvals offers only
          // the part a manager can act on. Both take the wording from
          // `presentLineStatus` rather than restating it.
          ...statusChips(
            ["requested", "approved", "ordered-vendor", "back-ordered", "received"],
            counts,
          ),
          { value: "all", label: "All" },
        ]}
      />

      {lines.length === 0 ? (
        <Empty
          tone={filter === "open" ? "good" : "neutral"}
          title={filter === "open" ? "Nothing outstanding" : "Nothing here"}
          body={
            filter === "open"
              ? "Every request has been dealt with. Tap Request to add more."
              : "No lines with that status."
          }
        />
      ) : (
        <div className="space-y-2">
          {lines.map((line) => (
            <Card
              key={line._id}
              /* The registry card lays its children out with `flex flex-col
                 gap-6`; this row lays itself out, and does so differently at
                 the two widths. Same rendered geometry the kit's <Card
                 padding="p-3"> produced. */
              className="block flex-row gap-0 p-3 lg:flex lg:flex-wrap lg:items-center lg:gap-x-4"
            >
              <div className="flex items-baseline gap-2 lg:min-w-0 lg:flex-1">
                {/* Right-aligned in a fixed column so ×1 and ×12 do not shunt
                    the item names past each other down a long list. */}
                <span className="num w-8 shrink-0 text-right font-bold">
                  ×{line.qty}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium">
                  {line.itemName}
                </span>
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-2 lg:mt-0 lg:shrink-0 lg:justify-end">
                {/* `contents` at 390 — the pills stay direct children of the
                    wrap row, so the phone layout is exactly what it was. At
                    `lg` the same span becomes a fixed column so a screenful of
                    statuses reads down the page instead of stepping about. */}
                <span className="contents lg:flex lg:w-72 lg:shrink-0 lg:items-center lg:gap-2">
                  <StatusPill status={line.lineStatus} />
                  {/* `UrgencyTag`, not a hand-rolled pill: this screen was
                      rendering the raw stored string, so the row read
                      "Approved" beside "urgent" — one capitalised status and
                      one lowercase database value dressed identically. The tag
                      capitalises it, drops "routine" entirely, and gives the
                      two levels different weights so urgency reads as a
                      qualifier on the status rather than a second status. */}
                  <UrgencyTag urgency={line.urgency} />
                </span>
                <span className="text-xs text-muted-foreground lg:w-56 lg:shrink-0 lg:text-right">
                  {viewer.clinicName ? "" : `${line.clinicName} · `}
                  {line.requestedByName} · {relativeDay(line.createdAt)}
                </span>
              </div>
              {line.vendorPromoNote ? (
                <p className="mt-2 rounded-md bg-blue-50 px-2 py-1 text-xs text-blue-900 lg:w-full">
                  {line.vendorPromoNote}
                </p>
              ) : null}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
